import { useRef } from "react";
import clsx from "clsx";
import { FileIcon, X } from "lucide-react";
import type {
  FileUploadCardProps,
  HandleFileChange,
  HandleRemoveFile,
} from "./types";

export const FileUploadCard = ({
  title,
  description = "Drag & Drop or Browse Files",
  acceptedFileTypes,
  icon,
  onFileSelect,
  multiple = false,
  className = "",
  value = [],
}: FileUploadCardProps) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const selectedFiles = value;

  const handleFileChange: HandleFileChange = (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length <= 0) return;

    const updatedFiles = multiple ? [...selectedFiles, ...files] : files;

    onFileSelect(updatedFiles);

    // Important for controlled inputs: allow selecting same file again
    event.target.value = "";
  };

  const handleRemoveFile: HandleRemoveFile = ({ fileToRemove, event }) => {
    event.stopPropagation();

    const updatedFiles = selectedFiles.filter((file) => file !== fileToRemove);

    onFileSelect(updatedFiles);
  };

  const handleClick = () => {
    inputRef.current?.click();
  };

  const acceptString = acceptedFileTypes.join(",");

  return (
    <div
      className={clsx(
        "flex flex-col rounded-lg border border-gray-200 overflow-hidden bg-white shadow-sm hover:shadow-md transition-shadow cursor-pointer",
        className,
      )}
      onClick={handleClick}
    >
      {/* Colored header bar */}
      <div className="h-2 bg-gradient-to-r from-blue-500 to-blue-600" />
      <div className="p-6 flex-1 flex flex-col items-center text-center gap-3">
        <div className="text-blue-600">{icon || <FileIcon size={32} />}</div>
        <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        <p className="text-sm text-gray-600">{description}</p>
        {selectedFiles.length > 0 ? (
          <div className="mt-auto flex flex-col gap-2 w-full max-h-48 overflow-y-auto">
            {selectedFiles.map((file) => (
              <div
                key={`${file.name}-${file.lastModified}`}
                className="flex items-center gap-2 p-3 bg-blue-50 rounded-md"
              >
                <FileIcon size={16} className="text-blue-600 flex-shrink-0" />
                <span className="text-sm font-medium text-blue-900 truncate flex-1">
                  {file.name}
                </span>
                <button
                  type="button"
                  onClick={(e) =>
                    handleRemoveFile({ fileToRemove: file, event: e })
                  }
                  className="text-gray-400 hover:text-red-500 transition-colors flex-shrink-0"
                  aria-label={`Remove ${file.name}`}
                >
                  <X size={16} />
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="mt-auto flex items-center justify-center gap-2 p-3 bg-gray-50 rounded-md w-full border border-dashed border-gray-300">
            <FileIcon size={16} className="text-gray-400 flex-shrink-0" />
            <span className="text-xs text-gray-500">
              {acceptedFileTypes.join(", ").toUpperCase()}
            </span>
          </div>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        onChange={handleFileChange}
        accept={acceptString}
        multiple={multiple}
        className="hidden"
        aria-label={`Upload ${title}`}
      />
    </div>
  );
};
